// ===================================================================
// src/app/routes.tsx
//
// Rotas da aplicação Sophia.
//
// - /login, /termos, /privacidade, /planos → públicas
// - /onboarding → protegida, layout próprio (sem sidebar)
// - /escola → protegida, AppLayout, guard de manager dentro da página
// - Todas as demais → protegidas por ProtectedRoute + AppLayout
// ===================================================================

import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router';
import AppLayout from './components/layout/AppLayout';
import OnboardingLayout from './components/layout/OnboardingLayout';
import ProtectedRoute from './components/auth/ProtectedRoute';
import StaffRoute from './components/auth/StaffRoute';
import { PermissionGuard } from './components/auth/PermissionGuard';
import RootRedirect from './components/auth/RootRedirect';
import Login from './pages/Login';

const AtendimentoConversas = lazy(() => import('./pages/atendimento/Conversas'));
const AtendimentoDocumentos = lazy(() => import('./pages/atendimento/Documentos'));
const AtendimentoMatriculaBoard = lazy(() => import('./pages/atendimento/MatriculaBoard'));
const AtendimentoCaptacaoLista = lazy(() => import('./pages/atendimento/CaptacaoLista'));
const AtendimentoCaptacaoBoard = lazy(() => import('./pages/atendimento/CaptacaoBoard'));
const AtendimentoCaptacaoConfig = lazy(() => import('./pages/atendimento/CaptacaoConfig'));
const AtendimentoConfigurar = lazy(() => import('./pages/atendimento/AtendimentoConfigurar'));
const AtendimentoTipos = lazy(() => import('./pages/atendimento/tipos/TiposSolicitacao'));
const AtendimentoCaptacaoVisaoGeral = lazy(() => import('./pages/atendimento/CaptacaoVisaoGeral'));
const AtendimentoCaptacaoFollowUp = lazy(() => import('./pages/atendimento/CaptacaoFollowUp'));
const BoardCompleto = lazy(() => import('./pages/BoardCompleto'));
const Profile = lazy(() => import('./pages/Profile'));
const Notificacoes = lazy(() => import('./pages/Notificacoes'));
const SchoolProfile = lazy(() => import('./pages/SchoolProfile'));
const ComponentLibrary = lazy(() => import('./pages/ComponentLibrary'));
const AgenteIA = lazy(() => import('./pages/AgenteIA'));
const Boards = lazy(() => import('./pages/Boards'));
const AgentConfig = lazy(() => import('./pages/AgentConfig'));
const Integracoes = lazy(() => import('./pages/Integracoes'));
const CalendarioEscolar = lazy(() => import('./pages/CalendarioEscolar'));
const TriageModeConfig = lazy(() => import('./pages/TriageModeConfig').then((m) => ({ default: m.TriageModeConfig })));
const Onboarding = lazy(() => import('./pages/Onboarding'));
const Billing = lazy(() => import('./pages/Billing'));
const Planos = lazy(() => import('./pages/Planos').then((m) => ({ default: m.Planos })));
const TermosDeUso = lazy(() => import('./pages/legal/TermosDeUso').then((m) => ({ default: m.TermosDeUso })));
const PoliticaDePrivacidade = lazy(() => import('./pages/legal/PoliticaDePrivacidade').then((m) => ({ default: m.PoliticaDePrivacidade })));
const EscolaPage = lazy(() => import('./pages/Escola'));
const Central = lazy(() => import('./pages/Central'));
const MinhaMesa = lazy(() => import('./pages/MinhaMesa'));
const Relatorios = lazy(() => import('./pages/Relatorios'));
const Suporte = lazy(() => import('./pages/Suporte'));
const Solicitacoes = lazy(() => import('./pages/Solicitacoes'));
const Tutoriais = lazy(() => import('./pages/tutoriais/Tutoriais'));
const TutorialDetalhe = lazy(() => import('./pages/tutoriais/TutorialDetalhe'));
const Chat = lazy(() => import('./pages/Chat'));
const ChecklistTemplatesPage = lazy(() => import('./pages/configuracoes/ChecklistTemplates'));
const ExtracurricularCoursesPage = lazy(() => import('./pages/configuracoes/ExtracurricularCourses'));
const MemoriaFamiliasPage = lazy(() => import('./pages/configuracoes/MemoriaFamilias'));
const LojaEstoque = lazy(() => import('./pages/loja/LojaEstoque'));
const LojaVenda = lazy(() => import('./pages/loja/LojaVenda'));
// ── Setor Pedagógico ──────────────────────────────────────────────────────────────────
// Import explícito de 'pedagogico/Pedagogico' (não './pages/Pedagogico') para não depender
// da resolução ambígua entre arquivo e diretório em filesystem case-insensitive (Windows).
const PedagogicoPage = lazy(() => import('./pages/pedagogico/Pedagogico'));
const PedagogicoAlunos = lazy(() => import('./pages/pedagogico/PedagogicoAlunos'));
const PedagogicoAlunoDetalhe = lazy(() => import('./pages/pedagogico/PedagogicoAlunoDetalhe'));
const PedagogicoResponsaveis = lazy(() => import('./pages/pedagogico/PedagogicoResponsaveis'));
const PedagogicoResponsavelDetalhe = lazy(() => import('./pages/pedagogico/PedagogicoResponsavelDetalhe'));
const PedagogicoTurmas = lazy(() => import('./pages/pedagogico/PedagogicoTurmas'));
const PedagogicoTurmaDetalhe = lazy(() => import('./pages/pedagogico/PedagogicoTurmaDetalhe'));
const PedagogicoProfessores = lazy(() => import('./pages/pedagogico/PedagogicoProfessores'));
const PedagogicoProfessorDetalhe = lazy(() => import('./pages/pedagogico/PedagogicoProfessorDetalhe'));
const PedagogicoMaterias = lazy(() => import('./pages/pedagogico/PedagogicoMaterias'));
const PedagogicoNotas = lazy(() => import('./pages/pedagogico/PedagogicoNotas'));
const AcceptInvite = lazy(() => import('./pages/AcceptInvite'));
const Signup      = lazy(() => import('./pages/Signup'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const SemAcesso    = lazy(() => import('./pages/SemAcesso'));
const AccountPending = lazy(() => import('./pages/AccountPending'));
const PendingSchools = lazy(() => import('./pages/internal/PendingSchools'));
const AdminDashboard = lazy(() => import('./pages/internal/AdminDashboard'));
const AdminPlans = lazy(() => import('./pages/internal/AdminPlans'));
const FinanceInterno = lazy(() => import('./pages/internal/FinanceInterno'));
const FeedbackInterno = lazy(() => import('./pages/internal/FeedbackInterno'));
const BoardsInterno = lazy(() => import('./pages/internal/BoardsInterno'));
const WorkspaceInterno = lazy(() => import('./pages/internal/Workspace'));
const TutoriaisAdmin = lazy(() => import('./pages/internal/TutoriaisAdmin'));
const ChatInterno = lazy(() => import('./pages/internal/ChatInterno'));
const NotFound = lazy(() => import('./pages/NotFound'));
const BuildDesatualizado = lazy(() => import('./pages/BuildDesatualizado'));
const ErroInesperado = lazy(() => import('./pages/ErroInesperado'));

const routeFallback = (
  <div className="flex items-center justify-center h-full w-full py-24">
    <div className="size-6 border-2 border-slate-300 border-t-indigo-600 rounded-full animate-spin" />
  </div>
);

const routeConfig: Parameters<typeof createBrowserRouter>[0] = [
  // ── Rotas públicas ──────────────────────────────────────────────────────────────────────────
  {
    path: '/login',
    Component: Login,
  },
  {
    path: '/termos',
    element: <Suspense fallback={routeFallback}><TermosDeUso /></Suspense>,
  },
  {
    path: '/privacidade',
    element: <Suspense fallback={routeFallback}><PoliticaDePrivacidade /></Suspense>,
  },
  {
    path: '/planos',
    element: <Suspense fallback={routeFallback}><Planos /></Suspense>,
  },
  {
    path: '/invite/:token',
    element: <Suspense fallback={routeFallback}><AcceptInvite /></Suspense>,
  },
  {
    path: '/signup',
    element: <Suspense fallback={routeFallback}><Signup /></Suspense>,
  },
  {
    path: '/verify-email',
    element: <Suspense fallback={routeFallback}><VerifyEmail /></Suspense>,
  },
  {
    path: '/conta-em-analise',
    element: <Suspense fallback={routeFallback}><AccountPending /></Suspense>,
  },

  // ── Rotas protegidas ───────────────────────────────────────────────────────────────────
  {
    Component: ProtectedRoute,
    children: [
      {
        Component: AppLayout,
        children: [
          {
            // Landing "/" — a antiga Visão Geral do Atendimento foi absorvida
            // pelo Início; aqui só redireciona (ver RootRedirect).
            path: '/',
            element: <RootRedirect />,
          },
          {
            // bypassRoles=['operator']: o inbox já é escopado por setor no
            // backend (SessionViewSet.get_queryset, eleveia-api) — o módulo
            // 'secretaria' aqui só bloqueava operadores de setores que nunca
            // tiveram esse módulo habilitado, mesmo com conversas reais
            // esperando resposta. Ver comentário em PermissionGuard.tsx.
            path: '/atendimento/conversas',
            element: <PermissionGuard module="secretaria" bypassRoles={['operator']}><AtendimentoConversas /></PermissionGuard>,
          },
          {
            // Passagem da IA para a equipe com prazo e dono — só gestão (a página
            // mostra o aviso para os outros papéis; a API responde 403).
            path: '/atendimento/configurar',
            element: <PermissionGuard module="secretaria" bypassRoles={['operator']}><AtendimentoConfigurar /></PermissionGuard>,
          },
          {
            // Para onde vai, prazo e card de cada tipo — gestão e coordenação.
            // O papel é a trava (API + página); o módulo não pode barrar a coordenação.
            path: '/atendimento/tipos',
            element: <PermissionGuard module="secretaria" bypassRoles={['operator', 'coordinator']}><AtendimentoTipos /></PermissionGuard>,
          },
          {
            path: '/atendimento/documentos',
            element: <PermissionGuard module="secretaria"><AtendimentoDocumentos /></PermissionGuard>,
          },
          {
            // Board novo da Matrícula (26/10/2026) — mesmo padrão da Captação, ver MatriculaBoard.tsx.
            path: '/atendimento/matriculas',
            element: <PermissionGuard module="secretaria"><AtendimentoMatriculaBoard /></PermissionGuard>,
          },
          {
            // Módulo próprio ('captacao') — ver comentário em NavigationSidebar.tsx.
            // Board novo em torno da visita (13/10/2026) — ver CaptacaoBoard.tsx.
            path: '/atendimento/captacao',
            element: <PermissionGuard module="captacao"><AtendimentoCaptacaoBoard /></PermissionGuard>,
          },
          {
            path: '/atendimento/captacao/lista',
            element: <PermissionGuard module="captacao"><AtendimentoCaptacaoLista /></PermissionGuard>,
          },
          {
            path: '/atendimento/captacao/visao-geral',
            element: <PermissionGuard module="captacao"><AtendimentoCaptacaoVisaoGeral /></PermissionGuard>,
          },
          {
            path: '/atendimento/captacao/follow-up',
            element: <PermissionGuard module="captacao"><AtendimentoCaptacaoFollowUp /></PermissionGuard>,
          },
          {
            path: '/atendimento/captacao/configurar',
            element: <PermissionGuard module="captacao"><AtendimentoCaptacaoConfig /></PermissionGuard>,
          },
          {
            path: '/inicio',
            Component: Central,
          },
          {
            path: '/minha-mesa',
            Component: MinhaMesa,
          },
          {
            path: '/dashboard',
            element: <Navigate to="/inicio" replace />,
          },
          {
            // 'boards' é tela básica, não restringível por setor — ver
            // ALWAYS_VISIBLE_MODULES em apps/schools/constants.py (eleveia-api)
            // e o comentário em NavigationSidebar.tsx ("Setores"/"Workspace").
            path: '/boards',
            Component: Boards,
          },
          {
            // Antiga "Visão Geral" de Setores — virou os chips de setor de /boards.
            path: '/boards/visao-geral',
            element: <Navigate to="/boards" replace />,
          },
          {
            path: '/boards/triagem',
            Component: Boards,
          },
          {
            path: '/workspace',
            Component: Boards,
          },
          {
            path: '/board/triagem',
            element: <Navigate to="/boards/triagem" replace />,
          },
          {
            path: '/board/:sector',
            Component: BoardCompleto,
          },
          {
            path: '/profile',
            Component: Profile,
          },
          {
            path: '/notificacoes',
            Component: Notificacoes,
          },
          {
            path: '/school/profile',
            Component: SchoolProfile,
          },
          {
            // Hub da escola em abas — acesso limitado a managers (guard na página)
            path: '/escola',
            element: <PermissionGuard module="escola"><EscolaPage /></PermissionGuard>,
          },
          {
            // SLA foi incorporado à seção "Roteamento" de /agent-config (ELE-262 → merge)
            path: '/escola/sla',
            element: <Navigate to="/atendimento/tipos" replace />,
          },
          {
            path: '/escola/checklists',
            element: <PermissionGuard module="escola"><ChecklistTemplatesPage /></PermissionGuard>,
          },
          {
            path: '/escola/cursos-extra',
            element: <PermissionGuard module="escola"><ExtracurricularCoursesPage /></PermissionGuard>,
          },
          {
            // Direito de acesso e exclusão (LGPD) sobre fatos memorizados — ver AgentConfig ?section=memoria
            path: '/escola/memoria',
            element: <PermissionGuard module="escola"><MemoriaFamiliasPage /></PermissionGuard>,
          },
          {
            path: '/agent-config',
            element: <PermissionGuard module="agente_ia"><AgentConfig /></PermissionGuard>,
          },
          {
            path: '/integracoes',
            element: <PermissionGuard module="escola"><Integracoes /></PermissionGuard>,
          },
          {
            path: '/calendario',
            element: <PermissionGuard module="calendario"><CalendarioEscolar /></PermissionGuard>,
          },
          {
            // Módulo próprio ('estoque') — ver comentário em NavigationSidebar.tsx.
            path: '/loja/estoque',
            element: <PermissionGuard module="estoque"><LojaEstoque /></PermissionGuard>,
          },
          {
            path: '/loja/venda',
            element: <PermissionGuard module="loja"><LojaVenda /></PermissionGuard>,
          },
          {
            // FaqManagerPage foi absorvida por /agent-config?section=faq (unificação FAQ)
            path: '/agent-config/faqs',
            element: <Navigate to="/agent-config?section=faq" replace />,
          },
          {
            path: '/school/settings',
            Component: TriageModeConfig,
          },
          {
            path: '/billing',
            Component: Billing,
          },
          {
            path: '/relatorios',
            element: <PermissionGuard module="relatorios"><Relatorios /></PermissionGuard>,
          },
          {
            path: '/suporte',
            Component: Suporte,
          },
          {
            // Tela de acompanhamento da gestão — substitui a Triagem. A
            // própria página redireciona quem não é gestor/coordenador.
            path: '/solicitacoes',
            Component: Solicitacoes,
          },
          {
            path: '/tutoriais',
            Component: Tutoriais,
          },
          {
            path: '/tutoriais/:slug',
            Component: TutorialDetalhe,
          },
          {
            // 'chat' é tela básica, não restringível por setor — ver
            // ALWAYS_VISIBLE_MODULES em apps/schools/constants.py (eleveia-api).
            path: '/chat',
            Component: Chat,
          },
          {
            path: '/chat/:groupId',
            Component: Chat,
          },
          // ── Setor Pedagógico ──
          {
            path: '/pedagogico',
            element: <PermissionGuard module="pedagogico"><PedagogicoPage /></PermissionGuard>,
          },
          {
            path: '/pedagogico/alunos',
            element: <PermissionGuard module="pedagogico"><PedagogicoAlunos /></PermissionGuard>,
          },
          {
            path: '/pedagogico/alunos/:id',
            element: <PermissionGuard module="pedagogico"><PedagogicoAlunoDetalhe /></PermissionGuard>,
          },
          {
            path: '/pedagogico/responsaveis',
            element: <PermissionGuard module="pedagogico"><PedagogicoResponsaveis /></PermissionGuard>,
          },
          {
            path: '/pedagogico/responsaveis/:id',
            element: <PermissionGuard module="pedagogico"><PedagogicoResponsavelDetalhe /></PermissionGuard>,
          },
          {
            path: '/pedagogico/turmas',
            element: <PermissionGuard module="pedagogico"><PedagogicoTurmas /></PermissionGuard>,
          },
          {
            path: '/pedagogico/turmas/:id',
            element: <PermissionGuard module="pedagogico"><PedagogicoTurmaDetalhe /></PermissionGuard>,
          },
          {
            path: '/pedagogico/professores',
            element: <PermissionGuard module="pedagogico"><PedagogicoProfessores /></PermissionGuard>,
          },
          {
            path: '/pedagogico/professores/:id',
            element: <PermissionGuard module="pedagogico"><PedagogicoProfessorDetalhe /></PermissionGuard>,
          },
          {
            path: '/pedagogico/materias',
            element: <PermissionGuard module="pedagogico"><PedagogicoMaterias /></PermissionGuard>,
          },
          {
            path: '/pedagogico/notas',
            element: <PermissionGuard module="pedagogico"><PedagogicoNotas /></PermissionGuard>,
          },
          // ── Área interna Eleve.IA — staff-only ──
          {
            Component: StaffRoute,
            children: [
              {
                path: '/interno/dashboard',
                Component: AdminDashboard,
              },
              {
                path: '/interno/escolas-pendentes',
                Component: PendingSchools,
              },
              {
                path: '/interno/planos',
                Component: AdminPlans,
              },
              {
                path: '/interno/financeiro',
                Component: FinanceInterno,
              },
              {
                path: '/interno/feedback',
                Component: FeedbackInterno,
              },
              {
                path: '/interno/boards',
                Component: BoardsInterno,
              },
              {
                path: '/interno/workspace',
                Component: WorkspaceInterno,
              },
              {
                path: '/interno/tutoriais',
                Component: TutoriaisAdmin,
              },
              {
                path: '/interno/chat',
                Component: ChatInterno,
              },
            ],
          },
        ],
      },
      {
        path: '/agente-ia',
        element: <Suspense fallback={routeFallback}><AgenteIA /></Suspense>,
      },
      {
        path: '/sem-acesso',
        element: <Suspense fallback={routeFallback}><SemAcesso /></Suspense>,
      },
      // ── Onboarding — layout próprio, sem sidebar ──────────────────────────────────────
      {
        Component: OnboardingLayout,
        children: [
          {
            path: '/onboarding',
            Component: Onboarding,
          },
        ],
      },
    ],
  },

  // ── Rotas de dev (sem proteção) ───────────────────────────────────────────────────────
  {
    path: '/components',
    element: <Suspense fallback={routeFallback}><ComponentLibrary /></Suspense>,
  },

  // ── Build desatualizado (chunk lazy falhou) ───────────────────────────────────────────
  {
    path: '/build-desatualizado',
    element: <Suspense fallback={routeFallback}><BuildDesatualizado /></Suspense>,
  },

  // ── 404 — catch-all, sempre por último ────────────────────────────────────────────────
  {
    path: '*',
    element: <Suspense fallback={routeFallback}><NotFound /></Suspense>,
  },
];

// Cada rota de topo ganha o mesmo errorElement — qualquer erro de render
// ou de loader cai numa página elegante em vez do ecrã cru do react-router.
export const router = createBrowserRouter(
  routeConfig.map((route) => ({
    errorElement: (
      <Suspense fallback={routeFallback}>
        <ErroInesperado />
      </Suspense>
    ),
    ...route,
  })),
);
